import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, AlertTriangle, Download, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { NeedTenant } from "../components/layout/AppShell";

function RadioCards({ value, onChange, options, testid }) {
  return (
    <RadioGroup value={value || ""} onValueChange={onChange} className="flex flex-wrap gap-3" data-testid={testid}>
      {options.map((o) => (
        <label key={o.value} className={`flex items-center gap-2 rounded-lg border px-3.5 py-2.5 text-sm cursor-pointer transition-colors ${value === o.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
          <RadioGroupItem value={o.value} className="sr-only" />
          {o.label}
        </label>
      ))}
    </RadioGroup>
  );
}

export default function BIRCenterPage() {
  const { user, tenant, activeTenantId } = useAuth();
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [profile, setProfile] = useState({});
  const [taxCfg, setTaxCfg] = useState(null);
  const [series, setSeries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [seriesDraft, setSeriesDraft] = useState({});
  const [reportRange, setReportRange] = useState({ from: new Date().toISOString().slice(0, 8) + "01", to: new Date().toISOString().slice(0, 10) });
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, p, tc, sr, br] = await Promise.all([
        api.get("/bir/status"), api.get("/bir/profile"), api.get("/bir/tax-config"),
        api.get("/bir/series"), api.get("/branches"),
      ]);
      setStatus(st.data);
      setProfile(p.data);
      setTaxCfg(tc.data);
      setSeries(sr.data);
      setBranches(br.data);
      setSeriesDraft(Object.fromEntries(sr.data.map((s) => [s.branch_id, { ...s }])));
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/bir/profile", profile);
      setProfile(data);
      toast.success("BIR profile saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveTax = async () => {
    try {
      await api.put("/bir/tax-config", taxCfg);
      toast.success("Tax configuration saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveSeries = async (branchId) => {
    const d = seriesDraft[branchId];
    try {
      await api.put("/bir/series", {
        branch_id: branchId === "main" ? null : branchId,
        prefix: d.prefix, series_year: Number(d.series_year),
        starting_number: Number(d.starting_number), width: Number(d.width),
      });
      toast.success("Invoice series saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const runReport = async () => {
    try {
      const { data } = await api.get("/tax-reports/summary", { params: { from_date: reportRange.from, to_date: reportRange.to } });
      setSummary(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/tax-reports/export-csv", {
        params: { from_date: reportRange.from, to_date: reportRange.to }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_register_${reportRange.from}_${reportRange.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice register exported");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setExporting(false); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  if (tenant && !tenant.bir_compliance_enabled) {
    return (
      <div className="py-24 text-center space-y-3" data-testid="bir-disabled-notice">
        <AlertTriangle className="h-10 w-10 mx-auto text-amber-400" />
        <h2 className="font-heading text-xl font-bold text-slate-900">BIR &amp; Tax mode is disabled</h2>
        <p className="text-sm text-slate-500 max-w-md mx-auto">The platform administrator can enable BIR compliance for this store in the Admin Console → Stores.</p>
      </div>
    );
  }

  const setP = (k, v) => setProfile({ ...profile, [k]: v });
  const missing = status?.checks.filter((c) => !c.ok) || [];

  return (
    <div className="space-y-6" data-testid="bir-center-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">BIR Compliance Center</h1>
          <p className="text-sm text-slate-500">
            Configuration status:{" "}
            {status?.complete
              ? <span className="font-semibold text-emerald-700">Complete</span>
              : <span className="font-semibold text-amber-700" data-testid="bir-overall-status">Incomplete</span>}
          </p>
        </div>
      </div>

      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 max-w-3xl">
        MatteusMx records the information from your actual BIR registration documents and applies it to invoices and reports.
        Filling these fields does not by itself make a business “BIR compliant” — registration and authorization depend on the taxpayer, the configured system, and applicable BIR requirements.
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="bir-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="profile" data-testid="bir-tab-profile">Business Profile</TabsTrigger>
          <TabsTrigger value="tax" data-testid="bir-tab-tax">Tax Configuration</TabsTrigger>
          <TabsTrigger value="series" data-testid="bir-tab-series">Invoice Series</TabsTrigger>
          <TabsTrigger value="registration" data-testid="bir-tab-registration">Registration</TabsTrigger>
          <TabsTrigger value="reports" data-testid="bir-tab-reports">Reports &amp; Export</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
              <h3 className="font-heading font-semibold text-slate-900">Configuration Checklist</h3>
              {status?.checks.map((c) => (
                <div key={c.key} className="flex items-center gap-2.5 text-sm" data-testid={`bir-check-${c.key}`}>
                  {c.ok
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                  <span className={c.ok ? "text-slate-700" : "text-slate-500"}>{c.label}</span>
                </div>
              ))}
            </div>
            {!status?.complete && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 space-y-4" data-testid="bir-action-required-card">
                <h3 className="font-heading font-semibold text-amber-900">Action Required</h3>
                <p className="text-sm text-amber-800">
                  {missing.length} item{missing.length === 1 ? " is" : "s are"} incomplete: {missing.map((m) => m.label).join(", ")}.
                  Complete your business and invoice settings before using MatteusMx for live sales.
                </p>
                <Button className="bg-slate-900" onClick={() => setTab("profile")} data-testid="bir-complete-config-btn">
                  Complete Configuration <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-2xl space-y-5">
            <h3 className="font-heading font-semibold text-slate-900">BIR Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Registered Name</Label><Input value={profile.registered_name || ""} onChange={(e) => setP("registered_name", e.target.value)} data-testid="bir-registered-name-input" /></div>
              <div className="space-y-1.5"><Label>Trade Name</Label><Input value={profile.trade_name || ""} onChange={(e) => setP("trade_name", e.target.value)} data-testid="bir-trade-name-input" /></div>
              <div className="space-y-1.5"><Label>TIN</Label><Input value={profile.tin || ""} onChange={(e) => setP("tin", e.target.value)} placeholder="XXX-XXX-XXX" className="font-mono" data-testid="bir-tin-input" /></div>
              <div className="space-y-1.5"><Label>Branch Code</Label><Input value={profile.branch_code || ""} onChange={(e) => setP("branch_code", e.target.value)} className="font-mono" data-testid="bir-branch-code-input" /></div>
              <div className="space-y-1.5"><Label>RDO Code</Label><Input value={profile.rdo_code || ""} onChange={(e) => setP("rdo_code", e.target.value)} className="font-mono" data-testid="bir-rdo-code-input" /></div>
              <div className="space-y-1.5"><Label>Business Address</Label><Input value={profile.business_address || ""} onChange={(e) => setP("business_address", e.target.value)} data-testid="bir-address-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>VAT Status</Label>
              <RadioCards value={profile.vat_status} onChange={(v) => setP("vat_status", v)} testid="bir-vat-status"
                options={[{ value: "vat", label: "VAT Registered" }, { value: "non_vat", label: "Non-VAT Registered" }]} />
            </div>
            <div className="space-y-2">
              <Label>Taxpayer Type</Label>
              <RadioCards value={profile.taxpayer_type} onChange={(v) => setP("taxpayer_type", v)} testid="bir-taxpayer-type"
                options={[{ value: "individual", label: "Individual" }, { value: "corporation", label: "Corporation" }, { value: "partnership", label: "Partnership" }]} />
            </div>
            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <RadioCards value={profile.invoice_type} onChange={(v) => setP("invoice_type", v)} testid="bir-invoice-type"
                options={[{ value: "vat_invoice", label: "VAT Invoice" }, { value: "non_vat_invoice", label: "Non-VAT Invoice" }]} />
            </div>
            <Button className="bg-slate-900" onClick={saveProfile} data-testid="bir-profile-save-btn">Save profile</Button>
          </div>
        </TabsContent>

        <TabsContent value="tax" className="mt-4">
          {taxCfg && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-xl space-y-5">
              <h3 className="font-heading font-semibold text-slate-900">Tax Rules — rule-driven, per store</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>VAT rate (%)</Label>
                  <Input type="number" step="0.01" min="0" value={(taxCfg.vat_rate * 100)} onChange={(e) => setTaxCfg({ ...taxCfg, vat_rate: Number(e.target.value) / 100 })} className="font-mono" data-testid="tax-vat-rate-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Senior/PWD discount (%)</Label>
                  <Input type="number" step="0.01" min="0" value={(taxCfg.senior_discount_rate * 100)} onChange={(e) => setTaxCfg({ ...taxCfg, senior_discount_rate: Number(e.target.value) / 100 })} className="font-mono" data-testid="tax-senior-rate-input" />
                </div>
              </div>
              {[
                ["vat_registered", "VAT Registered business"],
                ["allow_vat_exempt", "Allow VAT-exempt sales (Senior/PWD, exempt entities)"],
                ["allow_zero_rated", "Allow zero-rated sales"],
              ].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <Label>{label}</Label>
                  <Switch checked={!!taxCfg[k]} onCheckedChange={(v) => setTaxCfg({ ...taxCfg, [k]: v })} data-testid={`tax-${k}-toggle`} />
                </div>
              ))}
              <Button className="bg-slate-900" onClick={saveTax} data-testid="tax-config-save-btn">Save tax rules</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="series" className="mt-4 space-y-4">
          <p className="text-xs text-slate-500 max-w-2xl">Invoice numbers are issued by the system only — cashiers cannot edit them. Each branch keeps its own controlled, immutable series. Voided invoices are never deleted.</p>
          {(series.length ? series : [{ branch_id: "main", series_year: new Date().getFullYear(), prefix: "INV-", starting_number: 1, width: 6, current_number: 0 }]).map((s) => {
            const d = seriesDraft[s.branch_id] || s;
            const branchName = s.branch_id === "main" ? (branches[0]?.branch_name || "Main") : (branches.find((b) => b.branch_id === s.branch_id)?.branch_name || s.branch_id);
            return (
              <div key={s.branch_id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid={`series-card-${s.branch_id}`}>
                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-semibold text-slate-900">{branchName}</h4>
                  <p className="font-mono text-xs text-slate-500">
                    Current: <span className="font-bold text-slate-900">{s.prefix}{String(s.current_number).padStart(s.width, "0")}</span>
                    {" · "}Next: <span className="font-bold text-emerald-700">{s.prefix}{String(s.current_number + 1 > s.starting_number ? s.current_number + 1 : s.starting_number).padStart(s.width, "0")}</span>
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1"><Label className="text-xs">Series (year)</Label><Input type="number" className="w-24 h-9 font-mono" value={d.series_year} onChange={(e) => setSeriesDraft({ ...seriesDraft, [s.branch_id]: { ...d, series_year: e.target.value } })} data-testid={`series-year-${s.branch_id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Prefix</Label><Input className="w-24 h-9 font-mono" value={d.prefix} onChange={(e) => setSeriesDraft({ ...seriesDraft, [s.branch_id]: { ...d, prefix: e.target.value } })} data-testid={`series-prefix-${s.branch_id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Starting</Label><Input type="number" className="w-28 h-9 font-mono" value={d.starting_number} onChange={(e) => setSeriesDraft({ ...seriesDraft, [s.branch_id]: { ...d, starting_number: e.target.value } })} data-testid={`series-starting-${s.branch_id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Padding</Label><Input type="number" className="w-20 h-9 font-mono" min="1" max="12" value={d.width} onChange={(e) => setSeriesDraft({ ...seriesDraft, [s.branch_id]: { ...d, width: e.target.value } })} /></div>
                  <Button size="sm" className="bg-slate-900 h-9" onClick={() => saveSeries(s.branch_id)} data-testid={`series-save-${s.branch_id}`}>Save series</Button>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="registration" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-xl space-y-5">
            <h3 className="font-heading font-semibold text-slate-900">BIR Registration Status</h3>
            <RadioCards value={profile.registration_status || "not_configured"} onChange={(v) => setP("registration_status", v)} testid="bir-registration-status"
              options={[
                { value: "not_configured", label: "Not configured" },
                { value: "configuration_complete", label: "Configuration complete" },
                { value: "for_registration", label: "For registration" },
                { value: "registered", label: "Registered" },
              ]} />
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>TIN</Label><Input value={profile.tin || ""} onChange={(e) => setP("tin", e.target.value)} className="font-mono" /></div>
              <div className="space-y-1.5"><Label>RDO Code</Label><Input value={profile.rdo_code || ""} onChange={(e) => setP("rdo_code", e.target.value)} className="font-mono" /></div>
              <div className="space-y-1.5"><Label>Branch Code</Label><Input value={profile.branch_code || ""} onChange={(e) => setP("branch_code", e.target.value)} className="font-mono" /></div>
            </div>
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">POS / System Information</h4>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="space-y-1.5"><Label>System Name</Label><Input value="MatteusMx" disabled className="bg-slate-50" /></div>
                <div className="space-y-1.5"><Label>Version</Label><Input value="1.0.0" disabled className="bg-slate-50" /></div>
                <div className="space-y-1.5"><Label>Machine / Terminal</Label><Input value={profile.machine_terminal || ""} onChange={(e) => setP("machine_terminal", e.target.value)} placeholder="TERMINAL-01" className="font-mono" data-testid="bir-terminal-input" /></div>
              </div>
            </div>
            <Button className="bg-slate-900" onClick={saveProfile} data-testid="bir-registration-save-btn">Save registration details</Button>
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" className="w-40 h-9" value={reportRange.from} onChange={(e) => setReportRange({ ...reportRange, from: e.target.value })} data-testid="report-from-date" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" className="w-40 h-9" value={reportRange.to} onChange={(e) => setReportRange({ ...reportRange, to: e.target.value })} data-testid="report-to-date" /></div>
            <Button className="bg-slate-900 h-9" onClick={runReport} data-testid="report-run-btn">Run report</Button>
            <Button variant="outline" className="h-9" onClick={exportCsv} disabled={exporting} data-testid="report-export-csv-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
              Export CSV
            </Button>
          </div>
          {summary && (
            <div className="space-y-4" data-testid="bir-report-results">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Gross Sales", summary.gross_sales], ["VATable Sales", summary.vatable_sales],
                  ["VAT-Exempt Sales", summary.vat_exempt_sales], ["Zero-Rated Sales", summary.zero_rated_sales],
                  ["Output VAT", summary.output_vat], ["Discounts", summary.discounts],
                ].map(([label, v]) => (
                  <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="font-mono font-bold text-lg text-slate-950 mt-1">{peso(v)}</p>
                  </div>
                ))}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Invoices</p>
                  <p className="font-mono font-bold text-lg text-slate-950 mt-1">{summary.paid_count} <span className="text-xs font-normal text-slate-400">paid</span></p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Void / Refund</p>
                  <p className="font-mono font-bold text-lg text-slate-950 mt-1">{summary.voided_count} / {summary.refunded_count}</p>
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-100"><h4 className="font-heading font-semibold text-slate-900">Daily Sales</h4></div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {summary.daily_sales.map((d) => (
                        <TableRow key={d.date}><TableCell className="font-mono text-xs">{d.date}</TableCell><TableCell className="text-right font-mono text-sm">{peso(d.total)}</TableCell></TableRow>
                      ))}
                      {summary.daily_sales.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-8 text-sm text-slate-400">No sales in range.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-100"><h4 className="font-heading font-semibold text-slate-900">Voided / Refunded Invoices</h4></div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead>Invoice</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[...summary.voided_invoices.map((v) => ({ ...v, kind: "VOID" })), ...summary.refunded_invoices.map((v) => ({ ...v, kind: "REFUNDED" }))].map((v) => (
                        <TableRow key={v.invoice_number}>
                          <TableCell className="font-mono text-xs">{v.invoice_number}</TableCell>
                          <TableCell><span className={`rounded px-2 py-0.5 text-[10px] font-bold ${v.kind === "VOID" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>{v.kind}</span></TableCell>
                          <TableCell className="text-right font-mono text-sm">{peso(v.total_amount)}</TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-32 truncate">{v.reason || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {summary.voided_invoices.length + summary.refunded_invoices.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-slate-400">None in range.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
